import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import '@/App.css';
import '@/index.css';

import Header from './components/Header';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import ResultPage from './pages/ResultPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ConversationsPage from './pages/ConversationsPage';
import ConversationDetailPage from './pages/ConversationDetailPage';
import AdminPage from './pages/AdminPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import { Toaster } from './components/ui/sonner';

export const AuthContext = React.createContext();

const ProtectedRoute = ({ isAuthenticated, isLoading, children }) => {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading...</p>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

const AuthRoute = ({ isAuthenticated, isLoading }) => {
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/" />;
  return <AuthPage />;
};

function App() {
  const { user, isAuthenticated, isLoading, loginUser, logoutUser, refreshUser } = useAuth();

  const authContextValue = useMemo(
    () => ({ user, isAuthenticated, isLoading, loginUser, logoutUser, refreshUser }),
    [user, isAuthenticated, isLoading, loginUser, logoutUser, refreshUser]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#0A0A0A]">
          <Header />
          <Routes>
            <Route path="/auth" element={<AuthRoute isAuthenticated={isAuthenticated} isLoading={isLoading} />} />
            <Route path="/" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/result" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <ResultPage />
              </ProtectedRoute>
            } />
            <Route path="/conversations" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <ConversationsPage />
              </ProtectedRoute>
            } />
            <Route path="/conversation/:conversationId" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <ConversationDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <AdminPage />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
